import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import {
  listTemplates, listServiceTypes, createTemplate, updateTemplate, getTemplateVersion, createTemplateVersion,
  updateTemplateVersion, publishTemplateVersion, templatePreviewBlob, getRequirements, saveRequirements,
  openPdfBlob, fmtDateTime, apiError,
} from '../../Engagements/engagementsApi';
import '../Support/SupportConsole.css';
import '../../Engagements/Engagements.css';

const MERGE_FIELDS = [
  'client.displayName', 'client.shortName', 'client.partyDescription', 'client.legalName', 'client.address', 'client.signatoryName', 'client.signatoryTitle',
  'cataam.legalName', 'cataam.address', 'cataam.signatoryName', 'cataam.signatoryTitle', 'cataam.leadEngineerName', 'cataam.sourceIps', 'cataam.insuranceNote',
  'engagement.reference', 'engagement.serviceLabel', 'engagement.effectiveDate', 'engagement.intakeDate', 'engagement.startDate', 'engagement.endDate',
  'engagement.reportDate', 'engagement.retestWindowDays', 'engagement.feeAmount', 'engagement.paymentSchedule', 'engagement.invoiceDueDays',
  'engagement.governingLaw', 'engagement.courts', 'engagement.ndaTermYears', 'engagement.confidentialityCapNote',
  'intake.environment', 'intake.backupNote', 'intake.testingApproach', 'intake.exploitationDepth', 'intake.realRecordsAccess', 'intake.methodology',
  'intake.severityScheme', 'intake.reportAudience', 'intake.testingWindows', 'intake.fragileComponents', 'intake.testAccounts', 'intake.wafChanges', 'intake.outOfScope',
  '#assets … n asset reference env stack', '#contacts … role name phone email', '#providers … name services approvalNeeded reference', '^hasProviders',
];

/** Agreement templates, versions, preview and the per-service requirement matrix. */
export default function AgreementTemplates() {
  const { t } = useTranslation();
  const [templates, setTemplates] = useState([]);
  const [services, setServices] = useState([]);
  const [reqs, setReqs] = useState([]);
  const [editing, setEditing] = useState(null);   // { templateId, versionId|null, contentHtml, changeNote, requiresResign }
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState('templates');

  const load = async () => {
    setError('');
    try {
      const [tp, sv, rq] = await Promise.all([listTemplates(), listServiceTypes(), getRequirements()]);
      setTemplates(tp); setServices(sv); setReqs(rq);
    } catch (e) { setError(apiError(e, 'Could not load templates.')); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const toast = (title) => Swal.fire({ toast: true, position: 'top-end', icon: 'success', title, showConfirmButton: false, timer: 2000 });

  const newTemplate = async () => {
    const { value, isConfirmed } = await Swal.fire({
      title: 'New template',
      html: '<input id="tc" class="swal2-input" placeholder="CODE (e.g. DPA)"><input id="tn" class="swal2-input" placeholder="Name"><input id="tcat" class="swal2-input" placeholder="Category (AGREEMENT, NDA, …)">'
        + '<label style="font-size:13px"><input id="tcs" type="checkbox" checked> Requires Cataam countersignature</label>',
      showCancelButton: true,
      preConfirm: () => ({ code: document.getElementById('tc').value.trim().toUpperCase(), name: document.getElementById('tn').value, category: document.getElementById('tcat').value, countersignRequired: document.getElementById('tcs').checked }),
    });
    if (!isConfirmed) return;
    try { await createTemplate(value); toast('Template created'); load(); } catch (e) { setError(apiError(e, 'Could not create.')); }
  };

  const toggleActive = async (tp) => { try { await updateTemplate(tp.id, { active: !tp.active }); load(); } catch (e) { setError(apiError(e, 'Update failed.')); } };

  const startVersion = async (tp, fromVersion) => {
    let html = '';
    if (fromVersion) { try { html = (await getTemplateVersion(tp.id, fromVersion.id)).contentHtml || ''; } catch (e) { setError('Could not load version.'); return; } }
    setEditing({ templateId: tp.id, templateName: tp.name, versionId: fromVersion && fromVersion.status === 'DRAFT' ? fromVersion.id : null, contentHtml: html, changeNote: '', requiresResign: true });
  };

  const saveVersion = async (publish) => {
    setBusy(true); setError('');
    try {
      let v;
      if (editing.versionId) v = await updateTemplateVersion(editing.templateId, editing.versionId, editing);
      else v = await createTemplateVersion(editing.templateId, editing);
      if (publish) {
        const ok = await Swal.fire({ title: 'Publish this version?', text: 'Published content is frozen and hashed. New engagements will render it; the previous version is retired.', showCancelButton: true, confirmButtonText: 'Publish' });
        if (ok.isConfirmed) { await publishTemplateVersion(editing.templateId, v.id); toast('Published'); }
      } else toast('Draft saved');
      setEditing(null);
      load();
    } catch (e) { setError(apiError(e, 'Could not save the version.')); } finally { setBusy(false); }
  };

  const preview = async (tp, v) => { try { openPdfBlob(await templatePreviewBlob(tp.id, v.id)); } catch (e) { setError(apiError(e, 'Preview failed (is the HTML valid XHTML?).')); } };

  const reqOn = (svc, tid) => reqs.find((r) => r.serviceType === svc && r.templateId === tid && r.active);
  const toggleReq = (svc, tid) => {
    setReqs((prev) => {
      const i = prev.findIndex((r) => r.serviceType === svc && r.templateId === tid);
      if (i >= 0) { const c = [...prev]; c[i] = { ...c[i], active: !c[i].active }; return c; }
      return [...prev, { serviceType: svc, templateId: tid, required: true, sortOrder: prev.length, active: true }];
    });
  };
  const toggleRequired = (svc, tid) => setReqs((prev) => prev.map((r) => (r.serviceType === svc && r.templateId === tid ? { ...r, required: !r.required } : r)));
  const saveMatrix = async () => {
    setBusy(true);
    try { setReqs(await saveRequirements(reqs.filter((r) => r.active).map((r, i) => ({ ...r, sortOrder: i })))); toast('Requirements saved'); } catch (e) { setError(apiError(e, 'Could not save.')); } finally { setBusy(false); }
  };

  if (editing) {
    return (
      <div className="spc-page">
        <button className="spc-back" onClick={() => setEditing(null)}><i className="fas fa-arrow-left" /> Back</button>
        {error && <p className="spc-error">{error}</p>}
        <h2 style={{ fontSize: '1.1rem' }}>{editing.versionId ? 'Edit draft' : 'New version'} · {editing.templateName}</h2>
        <div className="eng-help" style={{ marginBottom: 8 }}>Body must be valid XHTML (close every tag, use &amp;amp;). Merge fields: {MERGE_FIELDS.map((f) => <span key={f} className="eng-chip">{`{{${f}}}`}</span>)}</div>
        <textarea value={editing.contentHtml} onChange={(e) => setEditing({ ...editing, contentHtml: e.target.value })} style={{ width: '100%', minHeight: '55vh', fontFamily: 'Menlo, Consolas, monospace', fontSize: 12, border: '1px solid #cbd5e1', borderRadius: 8, padding: 10 }} />
        <div className="eng-form" style={{ marginTop: 10 }}>
          <div className="eng-field"><label>Change note</label><input value={editing.changeNote} onChange={(e) => setEditing({ ...editing, changeNote: e.target.value })} /></div>
          <div className="eng-field"><label>Existing signatures</label>
            <select value={editing.requiresResign ? 'yes' : 'no'} onChange={(e) => setEditing({ ...editing, requiresResign: e.target.value === 'yes' })}>
              <option value="yes">Material change — re-signature needed on re-issue</option><option value="no">Editorial — earlier signatures stay valid</option>
            </select></div>
        </div>
        <div className="eng-modal-foot">
          <span />
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="eng-btn" disabled={busy} onClick={() => saveVersion(false)}>Save draft</button>
            <button className="eng-btn-primary" disabled={busy} onClick={() => saveVersion(true)}>Save &amp; publish</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="spc-page">
      {error && <p className="spc-error">{error}</p>}
      <div className="eng-tabs" style={{ marginTop: 0 }}>
        <button className={`eng-tab ${tab === 'templates' ? 'eng-tab-on' : ''}`} onClick={() => setTab('templates')}>{t('AdminEngagements.templates', 'Templates')}</button>
        <button className={`eng-tab ${tab === 'matrix' ? 'eng-tab-on' : ''}`} onClick={() => setTab('matrix')}>{t('AdminEngagements.matrix', 'Required per service')}</button>
      </div>

      {tab === 'templates' && (
        <>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 10 }}><button className="eng-btn-primary eng-btn-sm" onClick={newTemplate}><i className="fas fa-plus" /> New template</button></div>
          <div className="eng-grid">
            {templates.map((tp) => (
              <div key={tp.id} className="eng-card eng-card-static" style={{ opacity: tp.active ? 1 : 0.6 }}>
                <div className="eng-card-head">
                  <div><div className="eng-card-title">{tp.name}</div><div className="eng-card-ref">{tp.code} · {tp.category} · {tp.countersignRequired ? 'countersigned' : 'client-only'}</div></div>
                  <span className={`eng-pill ${tp.active ? 'eng-pill-active' : 'eng-pill-cancelled'}`}>{tp.active ? 'active' : 'inactive'}</span>
                </div>
                <div className="eng-card-desc">{tp.description}</div>
                <table className="eng-table">
                  <tbody>
                    {(tp.versions || []).map((v) => (
                      <tr key={v.id}>
                        <td>v{v.versionNo}</td>
                        <td><span className={`eng-pill ${v.status === 'PUBLISHED' ? 'eng-pill-active' : v.status === 'DRAFT' ? 'eng-pill-issued' : 'eng-pill-cancelled'}`}>{v.status}</span></td>
                        <td className="eng-help">{v.publishedAt ? fmtDateTime(v.publishedAt) : fmtDateTime(v.createdAt)}{v.changeNote ? ` · ${v.changeNote}` : ''}</td>
                        <td><div className="eng-doc-actions">
                          <button className="eng-btn eng-btn-sm" onClick={() => preview(tp, v)}>Preview</button>
                          <button className="eng-btn eng-btn-sm" onClick={() => startVersion(tp, v)}>{v.status === 'DRAFT' ? 'Edit' : 'Copy to new'}</button>
                        </div></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="eng-card-foot">
                  <button className="eng-btn-ghost eng-btn-sm" onClick={() => startVersion(tp, null)}><i className="fas fa-plus" /> Blank version</button>
                  <button className="eng-btn-ghost eng-btn-sm" onClick={() => toggleActive(tp)}>{tp.active ? 'Deactivate' : 'Activate'}</button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {tab === 'matrix' && (
        <div className="eng-panel">
          <table className="eng-table">
            <thead><tr><th>Service</th>{templates.filter((tp) => tp.active).map((tp) => <th key={tp.id}>{tp.code}</th>)}</tr></thead>
            <tbody>
              {services.map((sv) => (
                <tr key={sv.serviceType}>
                  <td><strong>{sv.name}</strong><div className="eng-help">{sv.serviceType}</div></td>
                  {templates.filter((tp) => tp.active).map((tp) => {
                    const r = reqOn(sv.serviceType, tp.id);
                    return (
                      <td key={tp.id}>
                        <label style={{ fontSize: '0.78rem' }}><input type="checkbox" checked={!!r} onChange={() => toggleReq(sv.serviceType, tp.id)} /> needed</label><br />
                        {r && <label style={{ fontSize: '0.74rem', color: '#64748b' }}><input type="checkbox" checked={!!r.required} onChange={() => toggleRequired(sv.serviceType, tp.id)} /> required for activation</label>}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
          <div className="eng-modal-foot"><span className="eng-help">Changes apply to engagements issued from now on.</span><button className="eng-btn-primary" disabled={busy} onClick={saveMatrix}>Save matrix</button></div>
        </div>
      )}
    </div>
  );
}
